/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { tqconstants } from "../utils/constants/TQConstants";
import { tqtypes } from "../public/TQTypes";

export class TQHttpRequest {
    private _url = "";
    private _authToken = "";
    private _headers: [tqconstants.HeaderKey, string][] = [];
    private _method: tqconstants.RequestMethod = tqconstants.RequestMethod.GET;
    private _body: any = "";
    private _taskType: tqtypes.TaskType;
    private _isBlob = false;

    public get isBlob(): boolean {
        return this._isBlob;
    }
    public set isBlob(value: boolean) {
        this._isBlob = value;
    }

    constructor(task: tqtypes.TaskType) {
        this._taskType = task;
    }

    public get url(): string {
        return this._url;
    }
    public set url(value: string) {
        this._url = value;
    }

    public get method(): tqconstants.RequestMethod {
        return this._method;
    }
    public set method(value: tqconstants.RequestMethod) {
        this._method = value;
    }

    public get headers(): [tqconstants.HeaderKey, string][] {
        return this._headers;
    }
    public set headers(value: [tqconstants.HeaderKey, string][]) {
        this._headers = value;
    }

    public get authToken(): string {
        return this._authToken;
    }
    public set authToken(value: string) {
        this._authToken = value;
    }

    public get body(): string {
        return this._body;
    }
    public set body(value: string | FormData | any) {
        this._body = value;
    }

    public get taskType(): tqtypes.TaskType {
        return this._taskType;
    }
    public set taskType(value: tqtypes.TaskType) {
        this._taskType = value;
    }
}

export class TQHttpResponse {
    private _status: tqconstants.ResponseStatus = 0; // => number 100–599
    private _statusText = "";
    private _headers: [tqconstants.HeaderKey | string, string][] = []; // => Headers
    private _url = "";
    private _body: any = "";
    private _taskType: tqtypes.TaskType;

    constructor(task: tqtypes.TaskType) {
        this._taskType = task;
    }

    public get status() {
        return this._status;
    }
    public set status(value) {
        this._status = value;
    }

    public get statusText(): string {
        return this._statusText;
    }
    public set statusText(value: string) {
        this._statusText = value;
    }

    public get headers(): [tqconstants.HeaderKey | string, string][] {
        return this._headers;
    }
    public set headers(value: [tqconstants.HeaderKey | string, string][]) {
        this._headers = value;
    }

    public get url(): string {
        return this._url;
    }
    public set url(value: string) {
        this._url = value;
    }

    public get body(): any {
        return this._body;
    }
    public set body(value: any) {
        this._body = value;
    }

    public get taskType(): tqtypes.TaskType {
        return this._taskType;
    }
    public set taskType(value: tqtypes.TaskType) {
        this._taskType = value;
    }
}

export class TQHttpConnector {
    private _request: TQHttpRequest;

    constructor(req: TQHttpRequest) {
        this._request = req;
    }

    public get request(): TQHttpRequest {
        return this._request;
    }
    public set request(value: TQHttpRequest) {
        this._request = value;
    }

    public static mapStatusNumberToStatusCode(status: number) {
        switch (status) {
            case 200:
                return tqconstants.ResponseStatus.OK;
            case 204:
                return tqconstants.ResponseStatus.NO_CONTENT;
            case 206:
                return tqconstants.ResponseStatus.PARTIAL_CONTENT;

            case 301:
                return tqconstants.ResponseStatus.REDIRECTED;
            case 302:
                return tqconstants.ResponseStatus.FOUND;
            case 304:
                return tqconstants.ResponseStatus.NOT_MODIFIED;
            case 305:
                return tqconstants.ResponseStatus.USE_PROXY;
            case 307:
                return tqconstants.ResponseStatus.REDIRECT_TEMP;
            case 308:
                return tqconstants.ResponseStatus.REDIRECT_PERMANENTLY;

            case 400:
                return tqconstants.ResponseStatus.BAD_REQUEST;
            case 401:
                return tqconstants.ResponseStatus.UNAUTHORIZED;
            case 403:
                return tqconstants.ResponseStatus.FORBIDDEN;
            case 404:
                return tqconstants.ResponseStatus.NOT_FOUND;
            case 405:
                return tqconstants.ResponseStatus.METHOD_NOT_ALLOWED;
            case 407:
                return tqconstants.ResponseStatus.PROXY_REQUIRED;

            case 408:
            case 0:
                return tqconstants.ResponseStatus.REQUEST_TIMEOUT;
            case 429:
                return tqconstants.ResponseStatus.TOO_MANY_REQUESTS;
            case 449:
                return tqconstants.ResponseStatus.RETRY_WITH;

            case 500:
                return tqconstants.ResponseStatus.SERVER_ERROR;
            case 501:
                return tqconstants.ResponseStatus.NOT_IMPLMENTED;
            case 502:
                return tqconstants.ResponseStatus.BAD_GATEWAY;
            case 503:
                return tqconstants.ResponseStatus.SERVICE_UNAVAILABLE;
            case 504:
                return tqconstants.ResponseStatus.GATEWAY_TIMEOUT;
            case 511:
                return tqconstants.ResponseStatus.NETWORK_AUTH_NEEDED;
            case 598:
                return tqconstants.ResponseStatus.NETWORK_READ_TIMEOUT;
            case 599:
                return tqconstants.ResponseStatus.NETWORK_CONNECT_TIMEOUT;
        }
        return tqconstants.ResponseStatus.SERVER_ERROR;
    }

    public async send() {
        const isBlob = this._request.isBlob;
        const response: TQHttpResponse = new TQHttpResponse(this._request.taskType);

        return await fetch(this._request.url, {
            method: this._request.method,
            headers: this._request.headers,
            body: (function (request: TQHttpRequest) {
                if (request.body != "") {
                    return request.body;
                } else {
                    return null;
                }
            })(this._request),
        })
            .then(_response => {
                response.status = TQHttpConnector.mapStatusNumberToStatusCode(_response.status);
                response.statusText = _response.statusText;

                _response.headers.forEach((value, key) => {
                    response.headers.push([key, value]);
                });

                response.url = _response.url;
                if (response.status >= tqconstants.ResponseStatus.REDIRECTED) {
                    return _response.text();
                }

                if (isBlob) {
                    return _response.blob();
                }
                return _response.json();
            })
            .then(data => {
                /* <-- data inferred as { data: T }*/
                response.body = data;
                return response;
            })
            .catch(err => {
                throw err;
            });
    }
}
